import Comments from "immigram-ui-cmon/src/components/Comments/Comments";
import Heading, {
  HeadingTag,
} from "immigram-ui-cmon/src/components/Heading/Heading";
import Paragraph from "immigram-ui-cmon/src/components/Paragraph/Paragraph";
import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import { useTranslation } from "next-i18next";
import { isValidElement, ReactNode, useContext, useMemo } from "react";
import { FieldValues } from "react-hook-form";
import { Path, useWatch } from "react-hook-form";

import { isAdmin } from "@/lib/auth/rbac";
import { UserContext } from "@/lib/contexts/userContext";
import useTransparentState from "@/lib/hook/useTransparentState";
import { ARRAY_FIELD_NAME } from "@/lib/hookForm";
import { getProseMirrorMarkers } from "@/lib/proseMirror";
import { StopWord } from "@/lib/stopWords";

import ControlledTextAreaWithRaisingLabel, {
  ControlledTextAreaProps,
} from "./ControlledTextAreaWithRaisingLabel";
import CompactParagraph from "../TextCut/CompactParagraph";

export interface ControlledTextAreaWithInlineHelpProps<T extends FieldValues>
  extends ControlledTextAreaProps<T> {
  heading?: ReactNode;
  description?: ReactNode;
  descriptionFull?: boolean;
  example?: ReactNode | string;
  commentThreadId?: string;
  useGrid?: boolean;
  className?: string;
  stopWordsList?: StopWord[];
  enableVoiceRecorder?: boolean;
}

const Example = (props: { example: ReactNode }) => {
  const { t } = useTranslation();
  return typeof props.example === "string" && props.example.length > 0 ? (
    <div className="mx-4">
      <Paragraph className="mt-2.5 mb-2 text-[14px] font-semibold text-black-800">
        {t("application:formElement.text.example")}
      </Paragraph>
      <Paragraph className="mb-5 text-[14px] text-black-800">
        {props.example}
      </Paragraph>
    </div>
  ) : isValidElement(props.example) ? (
    <div className="mx-4 mb-5">
      <Paragraph className="mt-2.5 mb-2 text-[14px] font-semibold text-black-800">
        {t("application:formElement.text.example")}
      </Paragraph>
      <div className="flex flex-col gap-y-5 text-[14px] text-black-800">
        {props.example}
      </div>
    </div>
  ) : null;
};

export default function ControlledTextAreaWithInlineHelp<
  T extends Record<string, any> = Record<string, any>
>(props: ControlledTextAreaWithInlineHelpProps<T>) {
  const ctx = useContext(UserContext);
  const { useGrid = true, className, control, fieldName, index } = props;
  const { example, ...restProps } = props;

  const disabled = props.freeze || props.disabled || isAdmin(ctx.ownUser);
  const [disabledState, setDisabledState] = useTransparentState(disabled);

  const canToggleDisabled = disabled && isAdmin(ctx.ownUser);

  const fieldValue = useWatch<T>({
    control,
    name: (index === undefined
      ? fieldName
      : `${ARRAY_FIELD_NAME}.${index}.${fieldName}`) as Path<T>,
  });

  const markersOrder = useMemo(() => {
    return getProseMirrorMarkers(fieldValue);
  }, [fieldValue]);

  return (
    <div className={className}>
      <div
        className={clsxm(
          useGrid &&
            "w-full flex-grow sm:grid sm:grid-cols-6 lg:mx-auto lg:grid-cols-12 lg:gap-8"
        )}
      >
        <div
          className={clsxm(
            "mb-2",
            props.commentThreadId ? "col-span-7" : "col-span-12"
          )}
        >
          {props.heading && (
            <Heading tag={HeadingTag.H7} className="mt-0 mb-1.5 text-xl">
              {props.heading}
            </Heading>
          )}
          {props.description && (
            <>
              {props.descriptionFull ? (
                <div className="text-black-600">{props.description}</div>
              ) : (
                <Comments.Content>
                  <CompactParagraph
                    descriptionNodeClassName="text-black-600"
                    description={props.description}
                  />
                </Comments.Content>
              )}
            </>
          )}
        </div>
      </div>
      {props.commentThreadId ? (
        <Comments.Thread
          useGrid={useGrid}
          name={props.commentThreadId}
          order={markersOrder}
        >
          {(args) => (
            <div className="pb-2.5">
              <div
                className={clsxm(
                  example && "rounded-2xl border border-gray-100"
                )}
              >
                <div className="-m-[1px] pb-2">
                  <ControlledTextAreaWithRaisingLabel
                    {...restProps}
                    example=""
                    stopWordsList={props.stopWordsList}
                    disabled={disabledState}
                    onDisabledToggle={
                      canToggleDisabled ? setDisabledState : undefined
                    }
                    onMarkerSelected={(id: string) => args.showMarker(id)}
                    markers={args.markers}
                    onTextSelectionUpdate={
                      disabledState ? args.addCommentForMarker : undefined
                    }
                    after={() => (fieldValue ? args.AddCommentIcon : null)}
                    enableVoiceRecorder={props.enableVoiceRecorder}
                  />
                </div>
                <Example example={example} />
              </div>
            </div>
          )}
        </Comments.Thread>
      ) : (
        <Comments.Content>
          <div className="pb-2.5">
            <div
              className={clsxm(example && "rounded-2xl border border-gray-100")}
            >
              <div className="-m-[1px] pb-2">
                <ControlledTextAreaWithRaisingLabel
                  {...restProps}
                  disabled={disabledState}
                  onDisabledToggle={
                    canToggleDisabled ? setDisabledState : undefined
                  }
                  enableVoiceRecorder={props.enableVoiceRecorder}
                />
              </div>
              <Example example={example} />
            </div>
          </div>
        </Comments.Content>
      )}
    </div>
  );
}
